import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Search, Loader2, FileJson, Pencil, Trash2, Copy } from 'lucide-react';
import { useGlobalAssistant } from '@/contexts/GlobalAssistantContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { schemaApi, type SchemaItem } from '@/services/schemaApi';

type SchemaTemplate = 'empty' | 'judge';

export function SchemasPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { registerPageContext } = useGlobalAssistant();
  const [schemas, setSchemas] = useState<SchemaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [schemaToDelete, setSchemaToDelete] = useState<SchemaItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Create dialog state
  const [newSchemaName, setNewSchemaName] = useState('');
  const [schemaTemplate, setSchemaTemplate] = useState<SchemaTemplate>('empty');
  const [creating, setCreating] = useState(false);

  // Register schemas context for global assistant
  useEffect(() => {
    registerPageContext({
      contextType: 'schemas',
      schemas: {
        schema_count: schemas.length,
        schema_names: schemas.map(s => s.name),
      },
    });
  }, [registerPageContext, schemas]);

  const fetchSchemas = async () => {
    try {
      setLoading(true);
      const data = await schemaApi.list();
      setSchemas(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch schemas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchemas();
  }, []);

  const filteredSchemas = schemas.filter((schema) => {
    return (
      !searchQuery ||
      schema.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      schema.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const handleCreate = async () => {
    if (!newSchemaName.trim()) {
      toast({
        title: 'Error',
        description: 'Name is required',
        variant: 'destructive',
      });
      return;
    }

    try {
      setCreating(true);
      const template =
        schemaTemplate === 'judge'
          ? schemaApi.createJudgeSchemaTemplate()
          : schemaApi.createEmptySchema();

      template.title = newSchemaName;

      const created = await schemaApi.create(newSchemaName, template);
      setCreateDialogOpen(false);
      setNewSchemaName('');
      setSchemaTemplate('empty');
      navigate(`/developer/schemas/${created.id}`);
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to create schema',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!schemaToDelete) return;
    try {
      setDeleting(true);
      // For now, we don't have a delete endpoint in contentApi
      // We'll just show a message
      toast({
        title: 'Not implemented',
        description: 'Schema deletion is not yet implemented',
        variant: 'destructive',
      });
      setDeleteDialogOpen(false);
      setSchemaToDelete(null);
    } catch (err) {
      setError(`Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container max-w-6xl py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Schemas</h1>
        <div className="flex gap-2">
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Schema
          </Button>
          <Button variant="outline" onClick={fetchSchemas}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Search */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search schemas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredSchemas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">
                  <p className="text-muted-foreground">
                    {searchQuery
                      ? 'No schemas match your search'
                      : 'No schemas found. Create one to get started.'}
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              filteredSchemas.map((schema) => (
                <TableRow key={schema.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileJson className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{schema.name}</p>
                        {schema.description && (
                          <p className="text-sm text-muted-foreground truncate max-w-[400px]">
                            {schema.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {schema.published_version && (
                        <Badge variant="outline">v{schema.published_version}</Badge>
                      )}
                      {schema.draft_version && schema.draft_version !== schema.published_version && (
                        <Badge variant="secondary">Draft</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">{formatDate(schema.updated_at)}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/developer/schemas/${schema.id}`)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit Schema</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSchemaToDelete(schema);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete Schema</TooltipContent>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Create Schema</DialogTitle>
            <DialogDescription>
              Create a new JSON Schema for structured output.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="schema-name">Schema Name</Label>
              <Input
                id="schema-name"
                value={newSchemaName}
                onChange={(e) => setNewSchemaName(e.target.value)}
                placeholder="my_custom_schema"
              />
            </div>

            <div className="space-y-3">
              <Label>Template</Label>
              <RadioGroup
                value={schemaTemplate}
                onValueChange={(v) => setSchemaTemplate(v as SchemaTemplate)}
                className="space-y-2"
              >
                <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-accent/50">
                  <RadioGroupItem value="empty" className="mt-0.5" />
                  <div>
                    <span className="font-medium">Empty Schema</span>
                    <p className="text-sm text-muted-foreground">
                      Start with a blank schema
                    </p>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-accent/50">
                  <RadioGroupItem value="judge" className="mt-0.5" />
                  <div>
                    <span className="font-medium">Judge Schema Template</span>
                    <p className="text-sm text-muted-foreground">
                      Includes overall_score, verdict, and reasoning fields
                    </p>
                  </div>
                </label>
              </RadioGroup>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating || !newSchemaName.trim()}>
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Create & Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Schema</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{schemaToDelete?.name}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default SchemasPage;
